import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useSettings } from '@/contexts/SettingsContext';
import { formatDateByPreference } from '@/utils/dateFormat';
import { decodeUnicodeEscapes } from '@/utils/decodeUnicode';
import { usePostHog } from '@posthog/react';
import { projectApi, getProjectOverview, getProjectSummary } from '../../api/projectApi';
import { getAuthHeaders } from '../../api/workosAuth';
import {
  getDashboardSummary,
  getDashboardActions,
  type DashboardProjectSummary,
  type DashboardAction,
} from '@/api/dashboardApi';
import { API_BASE_URL } from '../../config';
import MainLayout from '../../components/layout/MainLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { Section } from '@/components/ui/Section';
import { StatCard } from '@/components/ui/StatCard';
import { AlertBanner } from '@/components/ui/AlertBanner';
import { PanelLoading } from '@/components/ui/loading';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  TrashIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';
import {
  Upload,
  Plus,
  ArrowRight,
  ShieldAlert,
  AlertTriangle,
  Target,
  CheckCircle2,
  Clock,
  CalendarDays,
  FileText,
  Layers,
  Users,
  Hash,
  Milestone,
  FolderTree,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Helpers (same as Dashboard for consistency)
// ---------------------------------------------------------------------------

function statusLabel(raw: string): string {
  switch (raw) {
    case 'ok':
    case 'pass':
      return 'OK';
    case 'warning':
      return 'Warning';
    case 'critical':
    case 'fail':
      return 'Critical';
    default:
      return 'Unknown';
  }
}

function statusColor(raw: string): 'ok' | 'warning' | 'critical' | 'neutral' {
  switch (raw) {
    case 'ok':
    case 'pass':
      return 'ok';
    case 'warning':
      return 'warning';
    case 'critical':
    case 'fail':
      return 'critical';
    default:
      return 'neutral';
  }
}

function breakdownLine(parts: { count: number; label: string }[]): string {
  return parts.map((p) => `${p.count} ${p.label}`).join(' · ');
}

function relativeTime(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60_000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch {
    return '';
  }
}

function formatFileSize(bytes: number | null | undefined): string {
  if (bytes == null || bytes === 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Status strip – 5 stat cards matching Dashboard layout */
const ProjectStatusStrip: React.FC<{
  data: DashboardProjectSummary;
  onNav: (path: string) => void;
}> = ({ data, onNav }) => {
  const healthRaw = data.health.status;
  const qualityRaw = data.quality.status === 'fail' ? 'critical' : data.quality.status;
  const critTotal =
    data.critical_near_critical.critical_count +
    data.critical_near_critical.near_critical_count;

  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
      <StatCard
        label="Schedule Health"
        value={statusLabel(healthRaw)}
        delta={breakdownLine([
          { count: data.health.total_activities, label: 'Activities' },
        ])}
        status={statusColor(healthRaw)}
        action={
          <button onClick={() => onNav('/app/analytics/health')} className="text-xs text-primary hover:underline">
            View
          </button>
        }
      />
      <StatCard
        label="Critical Path"
        value={critTotal}
        delta={breakdownLine([
          { count: data.critical_near_critical.critical_count, label: 'Critical' },
          { count: data.critical_near_critical.near_critical_count, label: 'Near-Critical' },
        ])}
        status={
          data.critical_near_critical.critical_count > 0
            ? 'critical'
            : data.critical_near_critical.near_critical_count > 0
              ? 'warning'
              : 'ok'
        }
        action={
          <button onClick={() => onNav('/app/analytics/critical-path')} className="text-xs text-primary hover:underline">
            View
          </button>
        }
      />
      <StatCard
        label="Milestones"
        value={data.milestones.total}
        delta={breakdownLine([
          { count: data.milestones.at_risk, label: 'At Risk' },
          { count: data.milestones.overdue, label: 'Overdue' },
        ])}
        status={
          data.milestones.overdue > 0 ? 'critical' : data.milestones.at_risk > 0 ? 'warning' : 'ok'
        }
        action={
          <button onClick={() => onNav('/app/analytics/milestones')} className="text-xs text-primary hover:underline">
            View
          </button>
        }
      />
      <StatCard
        label="Data Quality"
        value={statusLabel(qualityRaw)}
        delta={breakdownLine([
          { count: data.quality.pass_count, label: 'OK' },
          { count: data.quality.warn_count, label: 'Warn' },
          { count: data.quality.fail_count, label: 'Fail' },
        ])}
        status={statusColor(qualityRaw)}
        action={
          <button onClick={() => onNav('/app/analytics/quality')} className="text-xs text-primary hover:underline">
            View
          </button>
        }
      />
      {data.baseline.has_baseline ? (
        <StatCard
          label="Baseline"
          value={data.baseline.baseline_name || 'Set'}
          delta={
            data.baseline.baseline_date
              ? `Set ${relativeTime(data.baseline.baseline_date)}`
              : undefined
          }
          status="ok"
          action={
            <button onClick={() => onNav('/app/baselines')} className="text-xs text-primary hover:underline">
              Manage
            </button>
          }
        />
      ) : (
        <div
          className="rounded-md border border-dashed border-amber-500/50 bg-amber-500/5 px-3 py-2 flex flex-col justify-center cursor-pointer hover:bg-amber-500/10 transition-colors"
          onClick={() => onNav('/app/baselines')}
        >
          <p className="text-[11px] font-medium text-amber-400 leading-tight">No Baseline</p>
          <p className="mt-0.5 text-xs font-semibold text-foreground leading-tight">
            Set baseline →
          </p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">Required for delta metrics</p>
        </div>
      )}
    </div>
  );
};

/** Next actions panel – reuses dashboard /actions endpoint */
const NextActionsPanel: React.FC<{
  actions: DashboardAction[];
  loading: boolean;
  onNav: (path: string) => void;
}> = ({ actions, loading, onNav }) => (
  <Section title="Next actions">
    {loading ? (
      <div className="space-y-2 py-1">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-12 bg-muted/50 rounded w-full animate-pulse" />
        ))}
      </div>
    ) : actions.length === 0 ? (
      <div className="flex items-center gap-2 py-4 justify-center">
        <CheckCircle2 className="h-4 w-4 text-primary" />
        <span className="text-xs text-muted-foreground">No actions required</span>
      </div>
    ) : (
      <ul className="space-y-2">
        {actions.map((a) => (
          <li key={a.id}>
            <button
              type="button"
              onClick={() => onNav(a.link)}
              className="w-full text-left rounded-md border border-border px-3 py-2 hover:bg-muted/50 transition-colors group"
            >
              <div className="flex items-start gap-2">
                <span className="mt-0.5 shrink-0">
                  {a.severity === 'critical' ? (
                    <ShieldAlert className="h-3.5 w-3.5 text-destructive" />
                  ) : a.severity === 'warning' ? (
                    <AlertTriangle className="h-3.5 w-3.5 text-amber-400" />
                  ) : (
                    <Target className="h-3.5 w-3.5 text-muted-foreground" />
                  )}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-foreground group-hover:text-primary transition-colors">
                    {a.title}
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">
                    {a.description}
                  </p>
                </div>
                <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </button>
          </li>
        ))}
      </ul>
    )}
  </Section>
);

/** Skeleton loader */
const DetailSkeleton: React.FC = () => (
  <div className="space-y-4 animate-pulse">
    <div className="h-8 bg-muted/50 rounded w-1/3" />
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="h-[68px] rounded-md border border-border bg-card" />
      ))}
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
      <div className="lg:col-span-3 space-y-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-8 bg-muted/50 rounded w-full" />
        ))}
      </div>
      <div className="lg:col-span-2 space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-10 bg-muted/50 rounded w-full" />
        ))}
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const ProjectDetail: React.FC = () => {
  const { projectId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const posthog = usePostHog();

  // Project & overview data
  const [project, setProject] = useState<any>(null);
  const [overview, setOverview] = useState<any>(null);
  const [snapshots, setSnapshots] = useState<any[]>([]);
  const [primaryLogoUrl, setPrimaryLogoUrl] = useState<string | null>(null);
  const [secondaryLogoUrl, setSecondaryLogoUrl] = useState<string | null>(null);
  const [logoBusy, setLogoBusy] = useState<{ primary: 'idle' | 'uploading' | 'deleting'; secondary: 'idle' | 'uploading' | 'deleting' }>({
    primary: 'idle',
    secondary: 'idle',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dashboard data (status strip + next actions)
  const [dashSummary, setDashSummary] = useState<DashboardProjectSummary | null>(null);
  const [dashActions, setDashActions] = useState<DashboardAction[]>([]);
  const [dashLoading, setDashLoading] = useState(true);

  // Upload modal state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadMode, setUploadMode] = useState<'update' | 'baseline'>('update');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [baselineName, setBaselineName] = useState('');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  // Remove snapshot state
  const [snapshotToRemove, setSnapshotToRemove] = useState<{ id: string; name: string } | null>(null);
  const [removingSnapshot, setRemovingSnapshot] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  // Open upload modal when arriving via ?addBaseline=1 or ?upload=1
  useEffect(() => {
    if (searchParams.get('addBaseline') === '1') {
      setShowUploadModal(true);
      setUploadMode('baseline');
      const next = new URLSearchParams(searchParams);
      next.delete('addBaseline');
      setSearchParams(next, { replace: true });
    } else if (searchParams.get('upload') === '1') {
      setShowUploadModal(true);
      setUploadMode('update');
      setUploadFile(null);
      setBaselineName('');
      setUploadError(null);
      const next = new URLSearchParams(searchParams);
      next.delete('upload');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Fetch project data
  useEffect(() => {
    const fetchProject = async () => {
      if (!projectId) return;
      try {
        setLoading(true);
        setError(null);

        const allProjects = await projectApi.getProjects();
        const listProject = Array.isArray(allProjects)
          ? allProjects.find(
              (p: { id?: string; import_id?: string }) =>
                p.id === projectId || String(p.id) === projectId ||
                p.import_id === projectId || String(p.import_id) === projectId,
            )
          : null;
        const displayName = listProject?.name ?? null;

        // 1. Try v1 project overview
        const overviewData = await getProjectOverview(projectId);
        if (overviewData && overviewData.name != null) {
          setOverview(overviewData);
          const activities = overviewData.activities || {};
          const resources = overviewData.resources || {};
          const xerCode = overviewData.code ?? overviewData.name ?? '';
          setProject({
            id: overviewData.id ?? projectId,
            name: displayName ?? overviewData.name ?? 'Project',
            code: xerCode,
            organization: overviewData.organization ?? '',
            startDate: overviewData.start_date ?? null,
            endDate: overviewData.end_date ?? null,
            activities: activities.total ?? 0,
            resources: resources.assigned ?? resources.total ?? 0,
            critical: activities.critical ?? 0,
          });
          return;
        }

        // 2. Try v1 project summary
        const summary = await getProjectSummary(projectId);
        if (summary && summary.name != null) {
          setOverview(summary);
          const xerCode = summary.code ?? summary.name ?? '';
          setProject({
            id: summary.id ?? projectId,
            name: displayName ?? summary.name ?? 'Project',
            code: xerCode,
            organization: summary.organization ?? '',
            startDate: summary.start_date ?? null,
            endDate: summary.end_date ?? null,
            activities: summary.total_activities ?? 0,
            resources: summary.resources?.assigned ?? summary.resources?.total ?? 0,
            critical: summary.critical_activities ?? 0,
          });
          return;
        }

        // 3. Fallback: use project from list
        if (listProject) {
          setProject({
            id: listProject.id ?? listProject.import_id ?? projectId,
            name: listProject.name,
            code: (listProject as { code?: string }).code ?? null,
            organization: listProject.organization ?? '',
            startDate: null,
            endDate: null,
            activities: 0,
            resources: 0,
            critical: 0,
          });
          return;
        }

        setError('Project not found');
      } catch (err) {
        console.error('Error fetching project:', err);
        setError('Failed to load project');
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [projectId]);

  // Fetch dashboard summary + actions for status strip and next actions
  useEffect(() => {
    if (!projectId || !project) return;
    let cancelled = false;
    const load = async () => {
      setDashLoading(true);
      try {
        const [s, a] = await Promise.all([
          getDashboardSummary(projectId),
          getDashboardActions(projectId),
        ]);
        if (!cancelled) {
          if (s && s.mode === 'project' && !(s as DashboardProjectSummary).empty) {
            setDashSummary(s as DashboardProjectSummary);
          }
          setDashActions(a);
        }
      } catch (err) {
        console.error('Dashboard data load error:', err);
      } finally {
        if (!cancelled) setDashLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [projectId, project]);

  const handleDownloadSnapshot = async (snapshotId: string, fileName: string) => {
    if (!projectId) return;
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(
        `${API_BASE_URL}/v1/snapshots/${encodeURIComponent(projectId)}/${snapshotId}/download`,
        { headers },
      );
      if (!res.ok) return;
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName?.endsWith('.xer') ? fileName : `${fileName || 'snapshot'}.xer`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch {
      // ignore
    }
  };

  const refreshSnapshots = async () => {
    if (!projectId) return;
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE_URL}/v1/snapshots/${projectId}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setSnapshots(data.snapshots || []);
      }
    } catch {
      setSnapshots([]);
    }
  };

  useEffect(() => {
    if (!projectId || !project) return;
    refreshSnapshots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, project]);

  // Fetch primary and secondary logos
  useEffect(() => {
    if (!projectId || !project) return;
    const fetchLogos = async () => {
      try {
        const headers = await getAuthHeaders();
        const res = await fetch(`${API_BASE_URL}/v1/projects/${encodeURIComponent(projectId)}/logos`, { headers });
        if (res.ok) {
          const data = await res.json();
          const base = API_BASE_URL.replace(/\/api\/?$/, '');
          setPrimaryLogoUrl(data.primary?.url ? `${base}${data.primary.url}` : null);
          setSecondaryLogoUrl(data.secondary?.url ? `${base}${data.secondary.url}` : null);
        } else {
          setPrimaryLogoUrl(null);
          setSecondaryLogoUrl(null);
        }
      } catch {
        setPrimaryLogoUrl(null);
        setSecondaryLogoUrl(null);
      }
    };
    fetchLogos();
     
  }, [projectId, project]);

  const handleRemoveSnapshot = async () => {
    if (!snapshotToRemove || !projectId) return;
    setRemovingSnapshot(true);
    setRemoveError(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(
        `${API_BASE_URL}/v1/snapshots/${snapshotToRemove.id}`,
        { method: 'DELETE', headers },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to remove snapshot');
      }
      setSnapshotToRemove(null);
      await refreshSnapshots();
    } catch (err) {
      setRemoveError(err instanceof Error ? err.message : 'Failed to remove snapshot');
    } finally {
      setRemovingSnapshot(false);
    }
  };

  const handleUploadSubmit = async () => {
    if (!projectId || !project || !uploadFile) return;
    if (uploadMode === 'baseline' && !baselineName.trim()) {
      setUploadError('Baseline name is required.');
      return;
    }
    setUploading(true);
    setUploadError(null);
    posthog?.capture('upload_started', {
      project_id: projectId,
      type: uploadMode === 'baseline' ? 'baseline' : 'update',
    });
    try {
      await projectApi.uploadXerNewProject(project.name, uploadFile, {
        projectId,
        isBaseline: uploadMode === 'baseline',
        description: uploadMode === 'baseline' ? baselineName.trim() : undefined,
        organization: project.organization || undefined,
      });
      setShowUploadModal(false);
      setUploadFile(null);
      setBaselineName('');
      await refreshSnapshots();
      const overviewData = await getProjectOverview(projectId);
      if (overviewData) setOverview(overviewData);
      // Refresh dashboard data too
      const [s, a] = await Promise.all([
        getDashboardSummary(projectId),
        getDashboardActions(projectId),
      ]);
      if (s && s.mode === 'project' && !(s as DashboardProjectSummary).empty) {
        setDashSummary(s as DashboardProjectSummary);
      }
      setDashActions(a);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const handleLogoUpload = async (role: 'primary' | 'secondary', e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !projectId) return;
    if (!file.type.startsWith('image/')) return;
    setLogoBusy((prev) => ({ ...prev, [role]: 'uploading' }));
    try {
      const headers = await getAuthHeaders();
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(
        `${API_BASE_URL}/v1/projects/${encodeURIComponent(projectId)}/logo?role=${role}`,
        { method: 'POST', headers, body: formData }
      );
      if (res.ok) {
        const data = await res.json();
        const base = API_BASE_URL.replace(/\/api\/?$/, '');
        if (role === 'primary') setPrimaryLogoUrl(data.url ? `${base}${data.url}` : null);
        else setSecondaryLogoUrl(data.url ? `${base}${data.url}` : null);
      }
    } finally {
      setLogoBusy((prev) => ({ ...prev, [role]: 'idle' }));
      e.target.value = '';
    }
  };

  const handleLogoDelete = async (role: 'primary' | 'secondary') => {
    if (!projectId) return;
    setLogoBusy((prev) => ({ ...prev, [role]: 'deleting' }));
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(
        `${API_BASE_URL}/v1/projects/${encodeURIComponent(projectId)}/logo?role=${role}`,
        { method: 'DELETE', headers }
      );
      if (res.ok) {
        if (role === 'primary') setPrimaryLogoUrl(null);
        else setSecondaryLogoUrl(null);
      }
    } finally {
      setLogoBusy((prev) => ({ ...prev, [role]: 'idle' }));
    }
  };

  const openImportUpdate = () => {
    setShowUploadModal(true);
    setUploadMode('update');
    setUploadFile(null);
    setBaselineName('');
    setUploadError(null);
  };

  // ----- Derived values -----
  const latestSnapshot = snapshots.find((s: { is_latest?: boolean }) => s.is_latest) || snapshots[0];
  const snapshotDate = latestSnapshot?.uploaded_at;
  const dataDate = overview?.data_date || latestSnapshot?.data_date;

  const computeDuration = () => {
    if (!project?.startDate || !project?.endDate) return null;
    const start = new Date(project.startDate);
    const end = new Date(project.endDate);
    if (start.toDateString() === end.toDateString()) return 'Same day';
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const years = Math.floor(diffDays / 365);
    const remainingAfterYears = diffDays % 365;
    const months = Math.floor(remainingAfterYears / 30);
    const days = remainingAfterYears % 30;
    const parts: string[] = [];
    if (years > 0) parts.push(`${years}y`);
    if (months > 0) parts.push(`${months}mo`);
    if (days > 0 || parts.length === 0) parts.push(`${days > 0 ? days : diffDays}d`);
    return parts.join(' ');
  };

  // ----- Loading state -----
  if (loading) {
    return (
      <MainLayout title="Project">
        <div className="p-4 lg:p-6 max-w-[1600px]">
          <DetailSkeleton />
        </div>
      </MainLayout>
    );
  }

  // ----- Error state -----
  if (error || !project) {
    return (
      <MainLayout title="Error">
        <div className="p-6 flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <p className="text-destructive mb-4">{error || 'Project not found'}</p>
            <Link to="/app/projects" className="text-primary hover:underline text-sm">
              ← Back to projects
            </Link>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout title={project.name}>
      <div className="p-4 lg:p-6 space-y-4 max-w-[1600px]">
        {/* ── A) Header ── */}
        <PageHeader
          title={project.name}
          subtitle={
            [project.organization, project.code]
              .filter(Boolean)
              .join(' · ') || undefined
          }
          actions={
            <div className="flex items-center gap-1.5 flex-wrap">
              <Button variant="outline" size="sm" onClick={openImportUpdate}>
                <Upload className="mr-1.5 h-3.5 w-3.5" />
                Import Update
              </Button>
              <Button variant="ghost" size="sm" onClick={() => navigate('/app/upload')}>
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                New Project
              </Button>
            </div>
          }
        />

        {/* ── B) Status strip ── */}
        {dashLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-[68px] rounded-md border border-border bg-card animate-pulse" />
            ))}
          </div>
        ) : dashSummary ? (
          <ProjectStatusStrip data={dashSummary} onNav={(p) => navigate(p)} />
        ) : snapshots.length === 0 ? (
          <AlertBanner variant="default" title="No data yet">
            Import a Primavera P6 XER file to begin schedule analysis.
            <Button variant="outline" size="sm" className="ml-3" onClick={openImportUpdate}>
              Import Update
            </Button>
          </AlertBanner>
        ) : null}

        {/* ── C) Two-column main content ── */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* Left column (60%) */}
          <div className="lg:col-span-3 space-y-4">
            {/* Overview panel */}
            <Section title="Overview">
              <div className="space-y-4">
                {/* Dates row */}
                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                  <OverviewRow
                    icon={<CalendarDays className="h-3.5 w-3.5" />}
                    label="Snapshot date"
                    value={
                      snapshotDate
                        ? formatDateByPreference(snapshotDate, settings.dateFormat, { showTime: false, timeZone: settings.timezone })
                        : '—'
                    }
                    sub={snapshotDate ? relativeTime(snapshotDate) : undefined}
                  />
                  <OverviewRow
                    icon={<Clock className="h-3.5 w-3.5" />}
                    label="Data date"
                    value={
                      dataDate
                        ? formatDateByPreference(dataDate, settings.dateFormat, { showTime: false, timeZone: settings.timezone })
                        : '—'
                    }
                  />
                  <OverviewRow
                    icon={<CalendarDays className="h-3.5 w-3.5" />}
                    label="Project start"
                    value={
                      project.startDate
                        ? formatDateByPreference(project.startDate, settings.dateFormat, { timeZone: settings.timezone })
                        : '—'
                    }
                  />
                  <OverviewRow
                    icon={<CalendarDays className="h-3.5 w-3.5" />}
                    label="Project finish"
                    value={
                      project.endDate
                        ? formatDateByPreference(project.endDate, settings.dateFormat, { timeZone: settings.timezone })
                        : '—'
                    }
                  />
                  <OverviewRow
                    icon={<Clock className="h-3.5 w-3.5" />}
                    label="Duration"
                    value={computeDuration() || '—'}
                  />
                </div>

                {/* Separator */}
                <div className="border-t border-border" />

                {/* Counts row */}
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                  <CountChip
                    icon={<Layers className="h-3 w-3" />}
                    label="Activities"
                    value={overview ? (overview?.activities?.total ?? project?.activities ?? 0) : '—'}
                  />
                  <CountChip
                    icon={<Users className="h-3 w-3" />}
                    label="Resources"
                    value={overview ? (overview?.resources?.total ?? project?.resources ?? 0) : '—'}
                  />
                  <CountChip
                    icon={<Hash className="h-3 w-3" />}
                    label="Calendars"
                    value={overview ? (overview?.total_calendars ?? 0) : '—'}
                  />
                  <CountChip
                    icon={<FileText className="h-3 w-3" />}
                    label="UDFs"
                    value={overview ? (overview?.total_udfs ?? 0) : '—'}
                  />
                  <CountChip
                    icon={<Milestone className="h-3 w-3" />}
                    label="Milestones"
                    value={overview ? (overview?.total_milestones ?? 0) : '—'}
                  />
                  {(overview?.total_activity_codes != null && overview.total_activity_codes > 0) && (
                    <CountChip
                      icon={<FolderTree className="h-3 w-3" />}
                      label="Activity codes"
                      value={overview.total_activity_codes}
                    />
                  )}
                </div>

                {/* Primary and secondary logos – compact, optional */}
                <div className="space-y-4 pt-1">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-medium text-muted-foreground w-24 shrink-0">Primary logo</span>
                    {primaryLogoUrl ? (
                      <img
                        src={primaryLogoUrl}
                        alt="Primary logo"
                        className="h-8 max-w-[120px] object-contain rounded border border-border"
                      />
                    ) : (
                      <div className="h-8 w-16 rounded border border-dashed border-border bg-muted/30 flex items-center justify-center">
                        <PhotoIcon className="h-3.5 w-3.5 text-muted-foreground" />
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <label className="cursor-pointer text-[11px] text-muted-foreground hover:text-foreground transition-colors">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleLogoUpload('primary', e)}
                          disabled={logoBusy.primary !== 'idle'}
                        />
                        {logoBusy.primary === 'uploading'
                          ? 'Uploading…'
                          : primaryLogoUrl
                            ? 'Change'
                            : 'Upload'}
                      </label>
                      {primaryLogoUrl && (
                        <button
                          type="button"
                          onClick={() => handleLogoDelete('primary')}
                          disabled={logoBusy.primary !== 'idle'}
                          className="text-[11px] text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50"
                        >
                          {logoBusy.primary === 'deleting' ? 'Deleting…' : 'Delete'}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-medium text-muted-foreground w-24 shrink-0">Secondary logo</span>
                    {secondaryLogoUrl ? (
                      <img
                        src={secondaryLogoUrl}
                        alt="Secondary logo"
                        className="h-8 max-w-[120px] object-contain rounded border border-border"
                      />
                    ) : (
                      <div className="h-8 w-16 rounded border border-dashed border-border bg-muted/30 flex items-center justify-center">
                        <PhotoIcon className="h-3.5 w-3.5 text-muted-foreground" />
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <label className="cursor-pointer text-[11px] text-muted-foreground hover:text-foreground transition-colors">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleLogoUpload('secondary', e)}
                          disabled={logoBusy.secondary !== 'idle'}
                        />
                        {logoBusy.secondary === 'uploading'
                          ? 'Uploading…'
                          : secondaryLogoUrl
                            ? 'Change'
                            : 'Upload'}
                      </label>
                      {secondaryLogoUrl && (
                        <button
                          type="button"
                          onClick={() => handleLogoDelete('secondary')}
                          disabled={logoBusy.secondary !== 'idle'}
                          className="text-[11px] text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50"
                        >
                          {logoBusy.secondary === 'deleting' ? 'Deleting…' : 'Delete'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </Section>

            {/* Snapshot history – primary table */}
            <Section
              title="Snapshot history"
              action={
                <span className="text-[11px] tabular-nums text-muted-foreground">
                  {snapshots.length} snapshot{snapshots.length !== 1 ? 's' : ''}
                </span>
              }
            >
              {snapshots.length === 0 ? (
                <div className="py-8 text-center space-y-3">
                  <Upload className="h-8 w-8 text-muted-foreground mx-auto" />
                  <p className="text-sm text-muted-foreground">No snapshots yet</p>
                  <Button variant="outline" size="sm" onClick={openImportUpdate}>
                    <Upload className="mr-1.5 h-3.5 w-3.5" />
                    Import update
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto -mx-3 -mb-3">
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="py-2 px-3 text-left font-medium text-muted-foreground uppercase tracking-wider">
                          Uploaded
                        </th>
                        <th className="py-2 px-3 text-left font-medium text-muted-foreground uppercase tracking-wider">
                          XER name
                        </th>
                        <th className="py-2 px-3 text-left font-medium text-muted-foreground uppercase tracking-wider">
                          Size
                        </th>
                        <th className="py-2 px-3 text-left font-medium text-muted-foreground uppercase tracking-wider">
                          Type
                        </th>
                        <th className="py-2 px-3 text-left font-medium text-muted-foreground uppercase tracking-wider">
                          Current
                        </th>
                        <th className="py-2 px-3 text-right font-medium text-muted-foreground uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {snapshots.map(
                        (s: {
                          id: string;
                          uploaded_at?: string;
                          file_name?: string;
                          snapshot_name?: string;
                          file_size?: number;
                          is_baseline?: boolean;
                          snapshot_type?: string;
                          is_latest?: boolean;
                        }) => {
                          const isCurrent = !!s.is_latest;
                          return (
                            <tr
                              key={s.id}
                              className={
                                isCurrent
                                  ? 'bg-primary/5 hover:bg-primary/10'
                                  : 'hover:bg-muted/50'
                              }
                            >
                              <td className="py-2 px-3 text-foreground whitespace-nowrap tabular-nums">
                                {s.uploaded_at
                                  ? formatDateByPreference(s.uploaded_at, settings.dateFormat, {
                                      showTime: true,
                                      timeZone: settings.timezone,
                                      use24Hour: settings.use24Hour,
                                    })
                                  : '—'}
                              </td>
                              <td className="py-2 px-3 text-foreground max-w-[240px] truncate" title={s.file_name || s.snapshot_name || ''}>
                                {decodeUnicodeEscapes(s.file_name || s.snapshot_name || '—')}
                              </td>
                              <td className="py-2 px-3 text-muted-foreground whitespace-nowrap tabular-nums">
                                {formatFileSize(s.file_size)}
                              </td>
                              <td className="py-2 px-3 text-muted-foreground">
                                {s.is_baseline || s.snapshot_type === 'baseline' ? 'Baseline' : 'Schedule'}
                              </td>
                              <td className="py-2 px-3">
                                {isCurrent ? (
                                  <span className="inline-flex items-center rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary leading-tight">
                                    Current
                                  </span>
                                ) : (
                                  <span className="text-muted-foreground">—</span>
                                )}
                              </td>
                              <td className="py-2 px-3 text-right">
                                <div className="flex items-center justify-end gap-2">
                                  {snapshots.length >= 2 && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        navigate(`/app/project-state/${projectId}/change-intelligence`)
                                      }
                                      className="inline-flex items-center gap-1 text-primary hover:underline"
                                      title="Compare snapshots"
                                    >
                                      Compare
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleDownloadSnapshot(
                                        s.id,
                                        s.file_name || s.snapshot_name || 'snapshot',
                                      )
                                    }
                                    className="inline-flex items-center gap-1 text-primary hover:underline"
                                    title="Download XER"
                                  >
                                    <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                                    Download
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setSnapshotToRemove({
                                        id: s.id,
                                        name: s.file_name || s.snapshot_name || 'this snapshot',
                                      })
                                    }
                                    className="inline-flex items-center gap-1 text-destructive hover:underline"
                                    title="Remove"
                                  >
                                    <TrashIcon className="h-3.5 w-3.5" />
                                    Remove
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        },
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </Section>
          </div>

          {/* Right column (40%) */}
          <div className="lg:col-span-2 space-y-4">
            {/* Next actions */}
            <NextActionsPanel
              actions={dashActions}
              loading={dashLoading}
              onNav={(p) => navigate(p)}
            />

            {/* Data freshness panel (replaces Budget Overview) */}
            <Section title="Data freshness">
              <div className="space-y-2">
                <FreshnessRow
                  label="Last import"
                  value={snapshotDate ? relativeTime(snapshotDate) : 'No imports'}
                />
                <FreshnessRow
                  label="Total snapshots"
                  value={String(snapshots.length)}
                />
                <FreshnessRow
                  label="Baseline"
                  value={
                    snapshots.some((s: { is_baseline?: boolean }) => s.is_baseline)
                      ? 'Set'
                      : 'Not set'
                  }
                  status={
                    snapshots.some((s: { is_baseline?: boolean }) => s.is_baseline)
                      ? 'ok'
                      : 'warning'
                  }
                />
              </div>
            </Section>

            {/* Baseline panel */}
            <Section title="Baseline">
              {(() => {
                const activeBaseline = snapshots.find(
                  (s: { is_baseline?: boolean }) => s.is_baseline === true,
                );
                if (activeBaseline) {
                  const rawName =
                    (activeBaseline as { snapshot_name?: string; file_name?: string })
                      .snapshot_name ||
                    (activeBaseline as { snapshot_name?: string; file_name?: string })
                      .file_name ||
                    'Baseline';
                  const name = decodeUnicodeEscapes(rawName);
                  const uploadedAt = (activeBaseline as { uploaded_at?: string }).uploaded_at;
                  return (
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Active baseline</span>
                        <span className="font-medium text-foreground">{name}</span>
                      </div>
                      {uploadedAt && (
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground">Uploaded</span>
                          <span className="text-foreground tabular-nums">
                            {formatDateByPreference(uploadedAt, settings.dateFormat, {
                              showTime: false,
                              timeZone: settings.timezone,
                            })}
                          </span>
                        </div>
                      )}
                      <button
                        onClick={() => navigate('/app/baselines')}
                        className="text-xs text-primary hover:underline"
                      >
                        Manage baselines →
                      </button>
                    </div>
                  );
                }
                return (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">
                      No baseline set. Set a baseline to enable change tracking and variance
                      analysis.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate('/app/baselines')}
                    >
                      Manage baselines
                    </Button>
                  </div>
                );
              })()}
            </Section>
          </div>
        </div>
      </div>

      {/* ── Remove snapshot confirmation ── */}
      <Dialog
        open={!!snapshotToRemove}
        onOpenChange={(open) => {
          if (!open) {
            setSnapshotToRemove(null);
            setRemoveError(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Remove snapshot</DialogTitle>
            <DialogDescription>
              Remove &quot;{snapshotToRemove?.name}&quot; from the list? It will no longer appear in
              snapshot history or in the baseline dropdown. The data is retained but hidden.
            </DialogDescription>
          </DialogHeader>
          {removeError && (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {removeError}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setSnapshotToRemove(null);
                setRemoveError(null);
              }}
              disabled={removingSnapshot}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRemoveSnapshot}
              disabled={removingSnapshot}
            >
              {removingSnapshot ? 'Removing…' : 'Remove'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Update schedule / Add baseline modal ── */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Import update</DialogTitle>
            <DialogDescription>
              Update the working schedule or add a named baseline. You can have multiple baselines.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="flex gap-2">
              <Button
                type="button"
                variant={uploadMode === 'update' ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setUploadMode('update');
                  setUploadError(null);
                }}
              >
                Update schedule
              </Button>
              <Button
                type="button"
                variant={uploadMode === 'baseline' ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setUploadMode('baseline');
                  setUploadError(null);
                }}
              >
                Add baseline
              </Button>
            </div>
            {uploadError && (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {uploadError}
              </div>
            )}
            {uploadMode === 'baseline' && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Baseline name *</label>
                <Input
                  value={baselineName}
                  onChange={(e) => {
                    setBaselineName(e.target.value);
                    setUploadError(null);
                  }}
                  placeholder="e.g. Contract baseline, March 2025"
                  className="bg-background"
                />
              </div>
            )}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                {uploadMode === 'update' ? 'XER file (new working schedule)' : 'XER file'}
              </label>
              <Input
                type="file"
                accept=".xer"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  setUploadFile(f || null);
                  setUploadError(null);
                }}
                className="bg-background"
              />
              {uploadFile && (
                <p className="text-xs text-muted-foreground">
                  {uploadFile.name} ({(uploadFile.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              )}
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowUploadModal(false);
                setUploadFile(null);
                setBaselineName('');
                setUploadError(null);
              }}
              disabled={uploading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUploadSubmit}
              disabled={!uploadFile || (uploadMode === 'baseline' && !baselineName.trim()) || uploading}
            >
              {uploading ? 'Uploading…' : uploadMode === 'update' ? 'Update schedule' : 'Upload baseline'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

// ---------------------------------------------------------------------------
// Micro-components
// ---------------------------------------------------------------------------

/** Row inside the Overview panel */
const OverviewRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
}> = ({ icon, label, value, sub }) => (
  <div className="flex items-center gap-2 min-w-0">
    <span className="text-muted-foreground shrink-0">{icon}</span>
    <span className="text-[11px] text-muted-foreground shrink-0 w-24">{label}</span>
    <span className="text-xs font-medium text-foreground tabular-nums truncate">{value}</span>
    {sub && (
      <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">({sub})</span>
    )}
  </div>
);

/** Count chip inside the Overview panel */
const CountChip: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string | number;
}> = ({ icon, label, value }) => (
  <div className="rounded-md border border-border bg-muted/30 px-2 py-1.5 text-center">
    <div className="flex items-center justify-center gap-1 text-muted-foreground mb-0.5">
      {icon}
      <span className="text-[10px] uppercase tracking-wider">{label}</span>
    </div>
    <p className="text-sm font-semibold tabular-nums text-foreground">{value}</p>
  </div>
);

/** Row inside the Data freshness panel */
const FreshnessRow: React.FC<{
  label: string;
  value: string;
  status?: 'ok' | 'warning';
}> = ({ label, value, status }) => (
  <div className="flex justify-between text-xs">
    <span className="text-muted-foreground">{label}</span>
    <span
      className={
        status === 'ok'
          ? 'font-medium text-primary'
          : status === 'warning'
            ? 'font-medium text-amber-400'
            : 'text-foreground'
      }
    >
      {value}
    </span>
  </div>
);

export default ProjectDetail;
